import { useTranslation } from 'react-i18next';
import {
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
} from 'recharts';
import type { SalesMetrics, SalesByModifier } from '../../../api/queries/analytics.queries';

interface SalesChartsProps {
  salesMetrics?: SalesMetrics;
  salesByModifier?: SalesByModifier;
}

const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#00C49F', '#FFBB28', '#FF8042', '#0088FE'];

export function SalesByHourChart({ data }: { data?: SalesMetrics['salesByHour'] }) {
  const { t } = useTranslation();

  if (!data || data.length === 0) {
    return <EmptyChart message={t('analytics.noDataAvailable')} />;
  }

  const chartData = data.map((item) => ({
    hour: `${item.hour}:00`,
    revenue: item.revenue,
    orders: item.orderCount,
  }));

  return (
    <ResponsiveContainer width="100%" height={300}>
      <AreaChart data={chartData}>
        <defs>
          <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#8884d8" stopOpacity={0.8}/>
            <stop offset="95%" stopColor="#8884d8" stopOpacity={0}/>
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
        <XAxis dataKey="hour" fontSize={12} />
        <YAxis fontSize={12} />
        <Tooltip
          formatter={(value, name) => [
            name === 'revenue' ? `฿${Number(value).toLocaleString()}` : value,
            name === 'revenue' ? t('analytics.revenue') : t('analytics.orders'),
          ]}
        />
        <Legend />
        <Area
          type="monotone"
          dataKey="revenue"
          stroke="#8884d8"
          fillOpacity={1}
          fill="url(#colorRevenue)"
          name={t('analytics.revenue')}
        />
        <Line
          type="monotone"
          dataKey="orders"
          stroke="#82ca9d"
          name={t('analytics.orders')}
          strokeWidth={2}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}

export function SalesByCategoryChart({ data }: { data?: SalesMetrics['salesByCategory'] }) {
  const { t, i18n } = useTranslation();

  if (!data || data.length === 0) {
    return <EmptyChart message={t('analytics.noDataAvailable')} />;
  }

  const getLangValue = (multiLang: any) => {
    return multiLang?.[i18n.language as 'en' | 'th'] || multiLang?.en || '';
  };

  const chartData = data.map((item) => ({
    name: getLangValue(item.categoryName),
    value: item.revenue,
    orders: item.orderCount,
  }));

  return (
    <ResponsiveContainer width="100%" height={300}>
      <PieChart>
        <Pie
          data={chartData}
          cx="50%"
          cy="50%"
          labelLine={false}
          outerRadius={100}
          fill="#8884d8"
          dataKey="value"
          label={({ name, percent }) => `${name} ${((percent || 0) * 100).toFixed(0)}%`}
        >
          {chartData.map((_, index) => (
            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
          ))}
        </Pie>
        <Tooltip
          formatter={(value) => [`฿${Number(value).toLocaleString()}`, t('analytics.revenue')]}
        />
        <Legend />
      </PieChart>
    </ResponsiveContainer>
  );
}

export function TopSellingItemsChart({ data }: { data?: SalesMetrics['topSellingItems'] }) {
  const { t, i18n } = useTranslation();

  if (!data || data.length === 0) {
    return <EmptyChart message={t('analytics.noDataAvailable')} />;
  }

  const getLangValue = (multiLang: any) => {
    return multiLang?.[i18n.language as 'en' | 'th'] || multiLang?.en || '';
  };

  const chartData = data.slice(0, 10).map((item) => ({
    name: getLangValue(item.itemName),
    revenue: item.revenue,
    quantity: item.quantity,
  }));

  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={chartData} layout="vertical">
        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
        <XAxis type="number" fontSize={12} />
        <YAxis dataKey="name" type="category" fontSize={11} width={120} />
        <Tooltip
          formatter={(value, name) => [
            name === 'revenue' ? `฿${Number(value).toLocaleString()}` : value,
            name === 'revenue' ? t('analytics.revenue') : t('analytics.quantity'),
          ]}
        />
        <Legend />
        <Bar dataKey="revenue" fill="#8884d8" name={t('analytics.revenue')} />
        <Bar dataKey="quantity" fill="#82ca9d" name={t('analytics.quantity')} />
      </BarChart>
    </ResponsiveContainer>
  );
}

export function PaymentMethodsChart({ data }: { data?: SalesMetrics['paymentMethods'] }) {
  const { t } = useTranslation();

  if (!data || data.length === 0) {
    return <EmptyChart message={t('analytics.noDataAvailable')} />;
  }

  const chartData = data.map((item) => ({
    name: item.method.replace('_', ' ').toUpperCase(),
    value: item.total,
    count: item.count,
  }));

  return (
    <ResponsiveContainer width="100%" height={250}>
      <PieChart>
        <Pie
          data={chartData}
          cx="50%"
          cy="50%"
          innerRadius={60}
          outerRadius={80}
          fill="#8884d8"
          paddingAngle={5}
          dataKey="value"
          label={({ name, percent }) => `${name} ${((percent || 0) * 100).toFixed(0)}%`}
        >
          {chartData.map((_, index) => (
            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
          ))}
        </Pie>
        <Tooltip
          formatter={(value) => [`฿${Number(value).toLocaleString()}`, t('analytics.total')]}
        />
      </PieChart>
    </ResponsiveContainer>
  );
}

export function ModifierDistributionChart({ data, type }: {
  data?: SalesByModifier['sweetness'] | SalesByModifier['sizes'];
  type: 'sweetness' | 'size'
}) {
  const { t } = useTranslation();

  if (!data || data.length === 0) {
    return <EmptyChart message={t('analytics.noDataAvailable')} />;
  }

  const chartData = data.map((item: any) => ({
    name: type === 'sweetness' ? `${item.level}%` : item.size.toUpperCase(),
    count: item.count,
  }));

  return (
    <ResponsiveContainer width="100%" height={200}>
      <BarChart data={chartData}>
        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
        <XAxis dataKey="name" fontSize={12} />
        <YAxis fontSize={12} />
        <Tooltip />
        <Bar dataKey="count" fill="#8884d8" name={t('analytics.count')} radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function EmptyChart({ message }: { message: string }) {
  return (
    <div className="h-[300px] flex items-center justify-center bg-secondary/20 rounded">
      <p className="text-sm text-muted-foreground">{message}</p>
    </div>
  );
}

export default function SalesCharts(_props: SalesChartsProps) {
  return null; // This is just a container for exports
}
